import React from 'react';
import { Popover } from '@fx-ui/fine-design';
import {
    ContactSelectDialogProps,
    ContactSelectDialog as ContactSelectDialogBase,
    DataType,
    ContactAvatar,
    ContactName,
    type ContactItem
} from '@fx-ui/jdy-design-biz';
import MemberName from '../member-name';
import { DeptType, RoleType } from '../../interfaces';
import ValueTag from '../value-tag';
import { authModel } from '../../models';
import { tracker } from '../../utils';
import { TRACKER } from '../../constants';
import { includes, join } from '@libs/shared';
import MemberCard from '../member-card';
import DeptCard from '../dept-card';
import RoleCard from '../role-card';

const ContactSelectDialog: React.FC<ContactSelectDialogProps> = (props) => {
    const {
        renderIcon: renderIconBase,
        renderName: renderNameBase,
        renderTag: renderTagBase,
        ...otherProps
    } = props;

    const hasPopover = (item: ContactItem) => {
        const { dataType } = item;
        return !(
            (dataType === DataType.dept && item.type === DeptType.corp_coop) ||
            (dataType === DataType.role &&
                includes([RoleType.corp_coop, RoleType.external], item.type)) ||
            dataType === DataType.dynamic
        );
    };

    const renderCard = (item: ContactItem) => {
        const { _id, dataType } = item;
        return dataType === DataType.member ? (
            <MemberCard memberId={_id} />
        ) : dataType === DataType.dept ? (
            <DeptCard deptId={_id} />
        ) : dataType === DataType.role ? (
            <RoleCard roleId={_id} />
        ) : null;
    };

    const renderName: ContactSelectDialogProps['renderName'] = (item) => {
        if (renderNameBase) {
            const name = renderNameBase(item);
            if (name !== false) {
                return name;
            }
        }
        if (item.dataType === DataType.member) {
            return <MemberName name={item.nickname} status={item.status} type={item.type} />;
        }
        return <ContactName dataType={item.dataType} item={item} />;
    };

    const renderIcon = (item: ContactItem, deptRecursion?: boolean, showPopover = true) => {
        if (renderIconBase) {
            const icon = renderIconBase(item, deptRecursion);
            if (icon !== false) {
                return icon;
            }
        }

        return (
            <div>
                <Popover
                    key={item._id}
                    {...(hasPopover(item) && showPopover ? {} : { visible: false })}
                    trigger="hover"
                    placement="bottom-left"
                    hasPadding={false}
                    arrow={false}
                    content={renderCard(item)}
                    onAfterPopupShow={() => {
                        tracker(TRACKER.MEMBER_CARD_SETTINGS_HOVER);
                    }}
                >
                    <span style={{ display: 'flex' }}>
                        <ContactAvatar
                            dataType={item.dataType}
                            item={item}
                            deptRecursion={deptRecursion}
                        />
                    </span>
                </Popover>
            </div>
        );
    };

    const renderTag: ContactSelectDialogProps['renderTag'] = (item, options) => {
        if (renderTagBase) {
            return renderTagBase(item, options);
        }

        const { _id } = item;
        const { recursion, ...otherOptions } = options;

        return (
            <ValueTag
                valueKey={_id}
                icon={renderIcon(item, recursion, false)}
                name={renderName(item)}
                tagOptions={otherOptions}
                popoverOptions={
                    hasPopover(item)
                        ? {
                              content: renderCard(item),
                              onAfterPopupShow: () => {
                                  tracker(TRACKER.MEMBER_CARD_SETTINGS_HOVER);
                              }
                          }
                        : undefined
                }
            />
        );
    };

    return (
        <ContactSelectDialogBase
            renderName={renderName}
            renderIcon={renderIcon}
            renderTag={renderTag}
            allowSearch={!authModel.isISVEncrypted}
            onSearchKeywordChange={(keyword) => {
                tracker(TRACKER.MEMBER_SEARCH, {
                    p1: 'all',
                    p2: join(keyword, ',')
                });
            }}
            onSearchResultAllCheck={() => {
                tracker(TRACKER.MEMBER_SEARCH_RESULT_ALL_CHECK, {
                    p1: 'all'
                });
            }}
            onAllSelectedView={(count) => {
                tracker(TRACKER.MEMBER_VIEW_ALL_SELECTED, {
                    p1: 'all',
                    p2: count
                });
            }}
            onSelectWidthChange={(width) => {
                tracker(TRACKER.MEMBER_WIDTH_CHANGE, {
                    p1: 'all',
                    p2: width
                });
            }}
            {...otherProps}
        />
    );
};

export default ContactSelectDialog;
